import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  User,
} from "lucide-react";
import { toast } from "@project/hooks/use-toast";
import hireflowLogo from "@project/assets/hireflow-logo.png";
import atsBg from "@project/assets/ats-bg.jpg";
import navigationRoutes from "@utils/navigationRoutes";
import CandidateAuthService from "@project/services/CandidateAuthService";

export default function CandidateLoginPage() {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showSignupPw, setShowSignupPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async () => {
    if (!email.trim()) {
      toast({ title: "Please enter your email", variant: "destructive" });
      return;
    }
    if (!password) {
      toast({ title: "Please enter your password", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await CandidateAuthService.login(email.trim(), password);
      navigate(navigationRoutes.ATS_DASHBOARD);
      toast({ title: "Welcome!", description: `Logged in as ${email}` });
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        err?.message ||
        "Login failed";
      toast({ title: "Login Failed", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    if (!firstName.trim()) {
      toast({ title: "Please enter your first name", variant: "destructive" });
      return;
    }
    if (!lastName.trim()) {
      toast({ title: "Please enter your last name", variant: "destructive" });
      return;
    }
    if (!signupEmail.trim()) {
      toast({ title: "Please enter your email", variant: "destructive" });
      return;
    }
    if (signupPassword.length < 6) {
      toast({
        title: "Password must be at least 6 characters",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      await CandidateAuthService.signup(
        signupEmail.trim(),
        signupPassword,
        firstName.trim(),
        lastName.trim()
      );
      toast({
        title: "Account Created!",
        description: "Welcome to HireFlow Candidate Portal.",
      });
      navigate(navigationRoutes.ATS_PROFILE_SETUP);
    } catch (err: any) {
      // Handle various error formats from API
      const errorData = err?.response?.data;
      let msg = "Signup failed";
      
      if (errorData?.password) {
        // Password validation errors (array)
        msg = Array.isArray(errorData.password) 
          ? errorData.password.join(". ") 
          : errorData.password;
      } else if (errorData?.email) {
        msg = Array.isArray(errorData.email) 
          ? errorData.email[0] 
          : errorData.email;
      } else if (errorData?.detail) {
        msg = errorData.detail;
      } else if (errorData?.message) {
        msg = errorData.message;
      } else if (err?.message) {
        msg = err.message;
      }
      
      toast({ title: "Signup Failed", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center p-4">
      {/* Background */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <img src={atsBg} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-background/70 backdrop-blur-[2px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="section-card space-y-6 p-6 sm:p-8">
          {/* Logo + Candidate Badge */}
          <div className="space-y-2 text-center">
            <div className="mb-2 flex items-center justify-center gap-2.5">
              <img
                src={hireflowLogo}
                alt="HireFlow"
                className="h-10 w-auto rounded-xl object-contain"
              />
              <div className="text-left">
                <h1 className="font-display text-xl font-bold text-foreground">
                  HireFlow
                </h1>
                <p className="text-[10px] text-muted-foreground">
                  Candidate Portal
                </p>
              </div>
            </div>

            <p className="text-[12px] text-muted-foreground">
              Sign in to your account or create a new one to get started
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 rounded-xl bg-muted/50 p-1">
            <button
              onClick={() => setMode("login")}
              className={`flex-1 rounded-lg py-2 text-sm font-medium transition-all ${
                mode === "login"
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode("signup")}
              className={`flex-1 rounded-lg py-2 text-sm font-medium transition-all ${
                mode === "signup"
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              New Candidate
            </button>
          </div>

          {mode === "login" ? (
            <>
              {/* Login Form */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[12px] font-medium">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="h-10 pl-10"
                      onKeyDown={e => e.key === "Enter" && handleLogin()}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-medium">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type={showPw ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="h-10 pl-10 pr-10"
                      onKeyDown={e => e.key === "Enter" && handleLogin()}
                    />
                    <button
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPw ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <Button
                  onClick={() => handleLogin()}
                  disabled={loading}
                  className="btn-glow h-10 w-full font-semibold"
                >
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
              </div>


            </>
          ) : (
            <>
              {/* Signup Form — exactly like image: Email, Password, First Name, Last Name */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[12px] font-medium">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={signupEmail}
                      onChange={e => setSignupEmail(e.target.value)}
                      className="h-10 pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-medium">
                    Password <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type={showSignupPw ? "text" : "password"}
                      placeholder="••••••••"
                      value={signupPassword}
                      onChange={e => setSignupPassword(e.target.value)}
                      className="h-10 pl-10 pr-10"
                    />
                    <button
                      onClick={() => setShowSignupPw(!showSignupPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showSignupPw ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-medium">First Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="First Name"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      className="h-10 pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-medium">Last Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Last Name"
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      className="h-10 pl-10"
                      onKeyDown={e => e.key === "Enter" && handleSignup()}
                    />
                  </div>
                </div>
                <Button
                  onClick={handleSignup}
                  disabled={loading}
                  className="btn-glow h-10 w-full font-semibold"
                >
                  {loading ? "Signing Up..." : "Sign Up"}
                </Button>
                <p className="text-center text-[12px] text-muted-foreground">
                  Already have an account?{" "}
                  <button
                    onClick={() => setMode("login")}
                    className="font-medium text-primary hover:underline"
                  >
                    Login
                  </button>
                </p>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
